import React from 'react';
import { WidgetProps } from '../../types';

const useDynamicScript = (url: any) => {
  const [ready, setReady] = React.useState(false);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    if (!url) {
      return;
    }

    const element = document.createElement('script');
    element.src = url;
    element.type = 'text/javascript';
    element.async = true;

    setReady(false);
    setFailed(false);

    element.onload = () => {
      console.log(`Dynamic Script Loaded: ${url}`);
      setReady(true);
    };

    element.onerror = () => {
      console.error(`Dynamic Script Error: ${url}`);
      setReady(false);
      setFailed(true);
    };

    document.head.appendChild(element);

    return () => {
      console.log(`Dynamic Script Removed: ${url}`);
      document.head.removeChild(element);
    };
  }, [url]);

  return {
    ready,
    failed,
  };
};

const FederatedComponent = React.memo(
  ({ url, scope, module, overrides, options, ...props }: any) => {
    const { ready, failed } = useDynamicScript(url);

    if (ready) {
      const o = (global as any).__webpack_require__
        ? (global as any).__webpack_require__.o
        : {};
      (window[scope] as any).override(Object.assign({ ...overrides }, o));
    }

    if (!ready) {
      return <p>Loading...</p>;
    }

    if (failed) {
      return <p>Failed to load dynamic script: {url}</p>;
    }

    const Component = React.lazy(
      async () =>
        await (window[scope] as any).get(module).then((factory: any) => {
          const Module = factory();
          return Module;
        })
    );

    return (
      <React.Suspense fallback="Loading System">
        <Component options={options} {...props} />
      </React.Suspense>
    );
  }
);

export const FederatedRenderer = ({ data, options }: WidgetProps) => (
  <FederatedComponent
    url={data.url}
    module={data.name}
    scope={data.name}
    options={options}
    overrides={data.overrides}
  />
);
